/**
 * MaintenanceOverlay — full-screen branded TIME OUT screen.
 *
 * Design: split layout, massive white "UNDER MAINTENANCE" headline +
 * BLK-orange "WE'LL BE BACK SOON" subline on the left, hero artwork on
 * the right. Mobile: stacks vertically, hero shrinks above the headline.
 *
 * Triggered by App.js when:
 *   1. Backend returns 503 {maintenance:true,...} from any /api/* call
 *   2. Backend is completely unreachable (502/network failure)
 *   3. App boots into an active maintenance window
 *
 * Polls /api/system/status every 15s and auto-dismisses when the flag flips.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Check, Send } from "lucide-react";
import { API } from "../../utils";

const POLL_INTERVAL_MS = 15000;
const BLK_LOGO_URL =
  "https://media.blkpacks.fun/uploads/blk_logo_gradient_transparent.png";

const MaintenanceOverlay = ({ initial = {}, onDismiss }) => {
  const [state, setState] = useState({
    title: initial.title || "UNDER MAINTENANCE",
    subtitle: "WE'LL BE BACK SOON",
    message:
      initial.message ||
      "We're upgrading BLK PACKS for you. Back online shortly.",
    eta: initial.eta || null,
  });
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Poll for "we're back" every 15s — auto-dismiss the moment admin
  // flips the toggle off so users don't have to refresh.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/system/status`, { timeout: 8000 });
        const m = res.data?.maintenance;
        if (cancelled) return;
        if (!m || !m.enabled) {
          onDismiss?.();
          return;
        }
        // Refresh copy in case admin edited the message live
        setState((s) => ({
          ...s,
          title: m.title || s.title,
          message: m.message || s.message,
          eta: m.eta || s.eta,
        }));
      } catch (e) {
        /* network still down — keep showing the overlay */
      }
    };
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/maintenance/subscribe`, { email });
      setSubmitted(true);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Could not save your email right now. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="maintenance-overlay"
      className="fixed inset-0 z-[9999] bg-black overflow-y-auto"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      {/* Subtle brand glow corners */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_30%,rgba(249,115,22,0.10),transparent_55%),radial-gradient(circle_at_85%_70%,rgba(251,191,36,0.08),transparent_55%)]" />

      <div className="relative min-h-full flex items-center justify-center px-6 py-10 sm:px-12 lg:px-16">
        <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

          {/* ── LEFT: copy + email capture ─────────────────────────── */}
          <div className="order-2 lg:order-1">
            <h1
              className="text-white font-black uppercase leading-[0.9] tracking-tight text-5xl sm:text-7xl lg:text-[88px]"
              data-testid="maintenance-headline"
            >
              {state.title || "UNDER"}{" "}
              {!state.title?.includes(" ") && (
                <span className="block">MAINTENANCE</span>
              )}
            </h1>

            <p
              className="brand-gradient-text font-black uppercase tracking-[0.18em] mt-6 text-base sm:text-xl"
              data-testid="maintenance-subtitle"
              style={{ letterSpacing: "0.18em" }}
            >
              {state.subtitle}
            </p>

            {state.message && (
              <p className="text-white/60 text-sm sm:text-base mt-4 leading-relaxed max-w-md">
                {state.message}
              </p>
            )}

            {state.eta && (
              <p
                className="text-amber-400 text-[11px] uppercase tracking-[0.22em] font-bold mt-4"
                data-testid="maintenance-eta"
              >
                ETA · {new Date(state.eta).toLocaleString()}
              </p>
            )}

            {/* Email capture */}
            <div className="mt-10 max-w-md">
              {!submitted ? (
                <>
                  <p className="text-white/55 text-xs sm:text-sm mb-3">
                    Sign up to get notified when we're back!
                  </p>
                  <form
                    onSubmit={submitEmail}
                    className="flex bg-white rounded-md overflow-hidden"
                    data-testid="maintenance-subscribe-form"
                  >
                    <input
                      type="email"
                      required
                      disabled={submitting}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="flex-1 bg-white text-black placeholder-black/40 px-4 py-3 text-sm focus:outline-none"
                      data-testid="maintenance-email-input"
                    />
                    <button
                      type="submit"
                      disabled={submitting || !email}
                      className="brand-gradient-btn text-black font-black text-xs sm:text-sm px-5 sm:px-7 uppercase tracking-[0.18em] disabled:opacity-50 flex items-center gap-2"
                      data-testid="maintenance-email-submit"
                    >
                      {submitting ? "..." : "Get Notified"}
                    </button>
                  </form>
                  {error && (
                    <p
                      className="text-red-400 text-xs mt-2"
                      data-testid="maintenance-email-error"
                    >
                      {error}
                    </p>
                  )}
                </>
              ) : (
                <div
                  className="bg-green-500/10 border border-green-500/40 text-green-400 rounded-md p-4 flex items-center gap-2"
                  data-testid="maintenance-subscribed-success"
                >
                  <Check size={18} />
                  <span className="font-bold text-sm">
                    You're on the list. We'll email you the moment we're back.
                  </span>
                </div>
              )}
            </div>

            {/* Real-time updates + social row */}
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <span className="text-white text-[11px] sm:text-xs font-black uppercase tracking-[0.22em] border-b-2 border-white/60 pb-1">
                Real Time Updates
              </span>
              <div className="flex items-center gap-3 text-white/70">
                <a
                  href="https://x.com/blkpacks"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="X (Twitter)"
                  className="hover:text-orange-400 transition-colors"
                  data-testid="maintenance-social-x"
                >
                  <XIcon />
                </a>
                <a
                  href="https://instagram.com/blkpacks"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                  className="hover:text-orange-400 transition-colors"
                  data-testid="maintenance-social-ig"
                >
                  <InstagramIcon />
                </a>
                <a
                  href="https://discord.gg/blkpacks"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Discord"
                  className="hover:text-orange-400 transition-colors"
                  data-testid="maintenance-social-discord"
                >
                  <DiscordIcon />
                </a>
              </div>
            </div>
          </div>

          {/* ── RIGHT: BLK Logo hero ──────────────────────────────── */}
          <div className="order-1 lg:order-2 flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-square">
              {/* Glow halo */}
              <div className="absolute inset-0 bg-[radial-gradient(circle,rgba(249,115,22,0.32)_0%,transparent_62%)] blur-2xl animate-pulse-slow" />
              {/* Spinning ring */}
              <div className="absolute inset-4 rounded-full border-2 border-orange-500/30 animate-spin-slow" />
              <div className="absolute inset-8 rounded-full border border-amber-400/20" />
              {/* Logo — now a true transparent PNG so the black box is gone */}
              <div className="relative w-full h-full flex items-center justify-center">
                <img
                  src={BLK_LOGO_URL}
                  alt="BLK PACKS"
                  className="w-3/4 h-3/4 object-contain"
                  style={{
                    filter: "drop-shadow(0 0 60px rgba(249,115,22,0.55))",
                  }}
                />
              </div>
              {/* TIME OUT chip badge */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 brand-gradient-btn text-black px-5 py-2 rounded-full font-black text-xs uppercase tracking-[0.22em] shadow-2xl">
                Time Out
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Local animations */}
      <style>{`
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-spin-slow { animation: spin-slow 22s linear infinite; }
        @keyframes pulse-slow { 0%,100% { opacity: .55; transform: scale(1); } 50% { opacity: .85; transform: scale(1.04); } }
        .animate-pulse-slow { animation: pulse-slow 3.6s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

// ─── Minimal social icon SVGs (no extra deps) ──────────────────────────
const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2H21.5l-7.51 8.59L23 22h-6.844l-5.36-7.05L4.49 22H1.226l8.04-9.2L1 2h7l4.86 6.42L18.244 2zm-2.4 18h1.86L7.235 4H5.234l10.61 16z" />
  </svg>
);
const InstagramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);
const DiscordIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.211.375-.444.864-.608 1.249-1.844-.276-3.68-.276-5.486 0-.164-.394-.405-.874-.617-1.249a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.834 19.834 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.42 0-1.333.956-2.42 2.157-2.42 1.21 0 2.176 1.096 2.157 2.42 0 1.335-.956 2.42-2.157 2.42zm7.975 0c-1.183 0-2.157-1.085-2.157-2.42 0-1.333.955-2.42 2.157-2.42 1.21 0 2.176 1.096 2.157 2.42 0 1.335-.946 2.42-2.157 2.42z" />
  </svg>
);

export default MaintenanceOverlay;
